<template>
  <div id="invoice-statistic">
    <div class="vx-row">
      <div class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 mb-base">
        <statistics-card-line
          icon="UsersIcon"
          :statistic="totalRegisteredUsers.value | k_formatter"
          :statisticTitle="totalRegisteredUsers.title"
          :chartData="totalRegisteredUsers.chart"
          type="area"
        />
      </div>

      <div class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 mb-base">
        <statistics-card-line
          icon="DollarSignIcon"
          :statistic="totalPaymentReceived.value | currency"
          :statisticTitle="totalPaymentReceived.title"
          :chartData="totalPaymentReceived.chart"
          color="success"
          type="area"
        />
      </div>

      <div class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 mb-base">
        <statistics-card-line
          icon="ShoppingCartIcon"
          :statistic="totalGeneratedInvoice.value | k_formatter"
          :statisticTitle="totalGeneratedInvoice.title"
          :chartData="totalGeneratedInvoice.chart"
          color="danger"
          type="area"
        />
      </div>
      <div class="vx-col w-full sm:w-1/2 md:w-1/2 lg:w-1/4 xl:w-1/4 mb-base">
        <statistics-card-line
          icon="ShoppingBagIcon"
          :statistic="totalGeneratedTransactions.value | k_formatter"
          :statisticTitle="totalGeneratedTransactions.title"
          :chartData="totalGeneratedTransactions.chart"
          :colors="['#28c770', '#ea5454']"
          color="warning"
          type="area"
        />
      </div>
    </div>
    <div class="vx-row">
      <div class="vx-col w-full md:w-2/3 mb-base">
        <vx-card :title="$t('Stat.RevenueCardTitle')">
          <div slot="no-body" class="p-6 pb-0">
            <div class="flex">
              <div class="mr-6">
                <p class="mb-1 font-semibold">{{$t('Stat.LastMonth')}}</p>
                <p class="text-3xl text-success">{{ revenueComparisonLastMonth| currency}}</p>
              </div>
              <div>
                <p class="mb-1 font-semibold">{{$t('Stat.ThisMonth')}}</p>
                <p class="text-3xl">{{ revenueComparisonThisMonth | currency}}</p>
              </div>
            </div>
            <vue-apex-charts
              type="line"
              height="266"
              :series="revenueComparisonLine.chart"
              :options="revenueComparisonLine.chartOptions"
            />
          </div>
        </vx-card>
      </div>

      <div class="vx-col w-full lg:w-1/3 lg:mt-0 mt-base">
        <vx-card :title="$t('Stat.CustomerCardTitle')">
          <div slot="no-body">
            <!-- CHART -->
            <vue-apex-charts
              type="pie"
              height="345"
              class="mt-10 mb-10"
              :options="totalCustomersData.chartOptions"
              :series="totalCustomersData.value"
            />

            <!-- CHART DATA -->
            <ul class="mb-1">
              <li
                v-for="(labels, i) in totalCustomersData.chartOptions.labels"
                :key="labels"
                class="flex justify-between py-3 px-6 border d-theme-border-grey-light border-solid border-r-0 border-l-0 border-b-0"
              >
                <span class="flex items-center">
                  <span
                    class="inline-block h-3 w-3 rounded-full mr-2"
                    :class="`bg-${totalCustomersData.chartOptions.colors[i] }`"
                  ></span>
                  <span class="font-semibold">{{ labels }}</span>
                </span>
                <span>{{ totalCustomersData.value[i] }}</span>
              </li>
            </ul>
          </div>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import InvoiceApi from "@/api/invoice";
import StaffApi from "@/api/staff";
import StatisticsCardLine from "@/components/statistics-cards/StatisticsCardLine.vue";
import VueApexCharts from "vue-apexcharts";
import ChangeTimeDurationDropdown from "@/components/ChangeTimeDurationDropdown.vue";

export default {
  components: {
    VueApexCharts,
    StatisticsCardLine,
    ChangeTimeDurationDropdown
  },
  data() {
    return {
      not_found: false,
      revenueComparisonLastMonth: 0,
      revenueComparisonThisMonth: 0,
      revenueComparisonLine: {
        chart: [
          {
            name: this.$t("Stat.ThisMonth"),
            data: []
          },
          {
            name: this.$t("Stat.LastMonth"),
            data: []
          }
        ],
        chartOptions: {
          chart: {
            vm: this,
            toolbar: { show: true },
            dropShadow: {
              enabled: true,
              top: 5,
              left: 0,
              blur: 4,
              opacity: 0.1
            }
          },
          stroke: {
            curve: "smooth",
            dashArray: [0, 8],
            width: [4, 2]
          },
          grid: {
            borderColor: "#e7e7e7"
          },
          legend: {
            show: false
          },
          colors: ["#F97794", "#b8c2cc"],
          fill: {
            type: "gradient",
            gradient: {
              shade: "dark",
              inverseColors: false,
              gradientToColors: ["#7367F0", "#b8c2cc"],
              shadeIntensity: 1,
              type: "horizontal",
              opacityFrom: 1,
              opacityTo: 1,
              stops: [0, 100, 100, 100]
            }
          },
          markers: {
            size: 0,
            hover: {
              size: 5
            }
          },
          xaxis: {
            labels: {
              style: {
                cssClass: "text-grey fill-current"
              }
            },
            axisTicks: {
              show: false
            },
            categories: ["01", "05", "09", "13", "17", "21", "26", "31"],
            axisBorder: {
              show: false
            }
          },
          yaxis: {
            tickAmount: 5,
            labels: {
              style: {
                cssClass: "text-grey fill-current"
              },
              formatter: this.revenueFormatter
            }
          },
          tooltip: {
            x: { show: false }
          }
        }
      },
      totalCustomersData: {
        value: [0, 0, 0],
        chartOptions: {
          labels: [
            this.$t("Stat.CustomerType1"),
            this.$t("Stat.CustomerType2"),
            this.$t("Stat.CustomerType3")
          ],
          dataLabels: {
            enabled: false
          },
          legend: { show: false },
          chart: {
            type: "pie",
            offsetY: 0,
            dropShadow: {
              enabled: false,
              blur: 5,
              left: 1,
              top: 1,
              opacity: 0.2
            },
            toolbar: {
              show: false
            }
          },
          stroke: {
            width: 5
          },
          colors: ["#7961F9", "#FF9F43", "#EA5455"],
          fill: {
            type: "gradient",
            gradient: {
              gradientToColors: ["#9c8cfc", "#FFC085", "#f29292"]
            }
          }
        }
      },
      totalGeneratedTransactions: {
        title: this.$t("Stat.TransactionTitle"),
        value: 0,
        chart: [
          {
            name: this.$t("Stat.TransactionSeriesName1"),
            data: []
          },
          {
            name: this.$t("Stat.TransactionSeriesName2"),
            data: []
          }
        ]
      },
      totalRegisteredUsers: {
        title: this.$t("Stat.RegisteredUsersTitle"),
        value: 0,
        chart: [
          {
            name: this.$t("Stat.RegisteredSeriesName"),
            data: []
          }
        ]
      },
      totalPaymentReceived: {
        title: this.$t("Stat.PaymentReceivedTitle"),
        value: 0,
        chart: [
          {
            currency: true,
            name: this.$t("Stat.PaymentReceivedSeriesName"),
            data: []
          }
        ]
      },
      totalGeneratedInvoice: {
        title: this.$t("Stat.GeneratedInvoiceTitle"),
        value: 0,
        chart: [
          {
            name: this.$t("Stat.GeneratedInvoiceSeriesName"),
            data: []
          }
        ]
      }
    };
  },
  computed: {},
  methods: {
    revenueFormatter: function(val) {
      return this.$options.filters.currency(val);
    },
    async fetchLoadData() {
      this.$vs.loading({
        color: "#000",
        scale: 0.45
      });

      let response = await StaffApi.getStats(this);

      if (!response.data.error) {
        this.totalRegisteredUsers.value = response.data.TotalExtendUser;
        this.totalRegisteredUsers.chart = [
          { data: response.data.ListExtendUser }
        ];

        this.totalPaymentReceived.value = response.data.TotalPaid;

        this.totalPaymentReceived.chart = [{ data: response.data.ListPaid }];

        this.totalGeneratedInvoice.value = response.data.TotalInvoice;
        this.totalGeneratedInvoice.chart = [
          { data: response.data.ListInvoice }
        ];

        this.totalGeneratedTransactions.value =
          response.data.TotalSuccessPaid + response.data.TotalPaidFail;
        this.totalGeneratedTransactions.chart = [
          { data: response.data.ListPaidSuccess },
          { data: response.data.ListPaidFail }
        ];

        this.totalCustomersData.value = [
          response.data.TotalStudentUser,
          response.data.TotalStaffAndAcademicUser,
          response.data.TotalExtendUser
        ];

        this.revenueComparisonLastMonth = response.data.TotalPaidLastMonth;
        this.revenueComparisonThisMonth = response.data.TotalPaidThisMonth;
        this.revenueComparisonLine.chart = [
          { data: response.data.ListPaidThisMonth },
          { data: response.data.ListPaidLastMonth }
        ];
      }

      setTimeout(() => {
        this.$vs.loading.close();
      }, 100);
    }
  },
  created() {
    this.fetchLoadData();
  },
  mounted() {}
};
</script>